import { create } from "zustand";
import { hub } from "./hub-client";

/**
 * Session auth store.
 *
 * Flow:
 *   1. User hits /login → redirected to AppSumo SSO with PKCE.
 *   2. AppSumo redirects back to /oauth/callback?code=... with state.
 *   3. Callback POSTs code to Hub `/oauth/session` which verifies with
 *      AppSumo, mints a Hub session JWT, sets it as an HttpOnly cookie.
 *   4. UI calls Hub `/v1/me` to populate the user state.
 */

export type UserTier = "free" | "plus";

export interface SessionUser {
  id: string;
  name?: string;
  email?: string;
  tier: UserTier;
}

interface AuthState {
  user: SessionUser | null;
  loaded: boolean;
  setUser: (user: SessionUser | null) => void;
  markLoaded: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loaded: false,
  setUser: (user) => set({ user }),
  markLoaded: () => set({ loaded: true }),
}));

/** Bootstrap — called once at app mount. Fetches `/v1/me` to hydrate state. */
export function useBootstrapAuth() {
  const setUser = useAuth((s) => s.setUser);
  const markLoaded = useAuth((s) => s.markLoaded);
  return async () => {
    try {
      const res = await hub.GET("/v1/me", {});
      if (res.response.ok) {
        setUser(res.data as SessionUser);
      }
    } catch {
      // Swallow — unauthenticated just means user is null.
    } finally {
      markLoaded();
    }
  };
}

// ---------------- OAuth helpers ----------------

const AUTH_CLIENT_ID =
  (import.meta.env.VITE_AUTH_CLIENT_ID as string | undefined) ??
  "hub-marketplace-web";
const AUTH_AUTHORIZE_URL =
  (import.meta.env.VITE_AUTH_AUTHORIZE_URL as string | undefined) ??
  "https://auth.appsumo.com/oauth/authorize";

export async function beginLogin(returnTo: string = "/"): Promise<string> {
  const state = crypto.randomUUID();
  const verifier = randomString(64);
  const challenge = await pkceChallenge(verifier);

  sessionStorage.setItem("oauth_state", state);
  sessionStorage.setItem("oauth_verifier", verifier);
  sessionStorage.setItem("oauth_return_to", returnTo);

  const url = new URL(AUTH_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", AUTH_CLIENT_ID);
  url.searchParams.set("redirect_uri", `${window.location.origin}/oauth/callback`);
  url.searchParams.set("scope", "openid profile plus wallet");
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

export interface ExchangeResult {
  ok: boolean;
  returnTo: string;
  error?: string;
}

export async function exchangeAuthCode(params: URLSearchParams): Promise<ExchangeResult> {
  const code = params.get("code");
  const state = params.get("state");
  const expectedState = sessionStorage.getItem("oauth_state");
  const verifier = sessionStorage.getItem("oauth_verifier");
  const returnTo = sessionStorage.getItem("oauth_return_to") ?? "/";

  sessionStorage.removeItem("oauth_state");
  sessionStorage.removeItem("oauth_verifier");
  sessionStorage.removeItem("oauth_return_to");

  if (!code || !state || !expectedState || state !== expectedState) {
    return { ok: false, returnTo, error: "state mismatch or missing code" };
  }
  if (!verifier) {
    return { ok: false, returnTo, error: "missing PKCE verifier (refresh / tab closed?)" };
  }

  const res = await hub.POST("/oauth/session", {
    body: {
      code,
      code_verifier: verifier,
      redirect_uri: `${window.location.origin}/oauth/callback`,
    },
  });
  if (!res.response.ok) {
    const text = await res.response.text().catch(() => "");
    return { ok: false, returnTo, error: `session exchange failed: ${text.slice(0, 200)}` };
  }
  return { ok: true, returnTo };
}

export async function logout(): Promise<void> {
  try {
    await hub.POST("/oauth/session/logout", { body: {} });
  } catch {
    /* ignore — cookie may already be gone */
  }
  useAuth.getState().setUser(null);
}

// ---------------- PKCE helpers ----------------

function randomString(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes).slice(0, len);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return toBase64Url(new Uint8Array(digest));
}

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
